import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

import { PublicLayout } from '@/layouts/PublicLayout';
import { WalletLayout } from '@/layouts/WalletLayout';
import { AdminLayout } from '@/layouts/AdminLayout';

import LandingPage from '@/pages/public/LandingPage';
import AboutPage from '@/pages/public/AboutPage';
import ServicesPage from '@/pages/public/ServicesPage';
import DocumentsPage from '@/pages/public/DocumentsPage';
import DocumentDetailPage from '@/pages/public/DocumentDetailPage';
import ContactPage from '@/pages/public/ContactPage';
import BusinessPage from '@/pages/public/BusinessPage';

import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';

import DashboardPage from '@/pages/wallet/DashboardPage';
import ExchangePage from '@/pages/wallet/ExchangePage';
import TransactionsPage from '@/pages/wallet/TransactionsPage';
import DepositPage from '@/pages/wallet/DepositPage';
import WithdrawPage from '@/pages/wallet/WithdrawPage';
import KycPage from '@/pages/wallet/KycPage';
import ProfilePage from '@/pages/wallet/ProfilePage';

import { BusinessLayout } from '@/layouts/BusinessLayout';
import BusinessRegisterPage from '@/pages/business/BusinessRegisterPage';
import BusinessDashboardPage from '@/pages/business/BusinessDashboardPage';
import MassPaymentsPage from '@/pages/business/MassPaymentsPage';
import TemplatesPage from '@/pages/business/TemplatesPage';
import InvoicesPage from '@/pages/business/InvoicesPage';

import AdminLoginPage from '@/pages/admin/AdminLoginPage';
import AdminOverviewPage from '@/pages/admin/AdminOverviewPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminKycQueuePage from '@/pages/admin/AdminKycQueuePage';
import AdminRatesPage from '@/pages/admin/AdminRatesPage';
import AdminDepositsPage from '@/pages/admin/AdminDepositsPage';
import AdminWithdrawalsPage from '@/pages/admin/AdminWithdrawalsPage';
import AdminTransactionsPage from '@/pages/admin/AdminTransactionsPage';
import AdminAuditLogPage from '@/pages/admin/AdminAuditLogPage';
import AdminAdminsPage from '@/pages/admin/AdminAdminsPage';
import AdminCompaniesPage from '@/pages/admin/AdminCompaniesPage';
import AdminAmlPage from '@/pages/admin/AdminAmlPage';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[hsl(var(--muted-foreground))]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/business" element={<BusinessPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/documents/:docId" element={<DocumentDetailPage />} />
              <Route path="/contact" element={<ContactPage />} />
            </Route>

            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            <Route path="/wallet" element={<RequireAuth><WalletLayout /></RequireAuth>}>
              <Route index element={<DashboardPage />} />
              <Route path="exchange" element={<ExchangePage />} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="deposit" element={<DepositPage />} />
              <Route path="withdraw" element={<WithdrawPage />} />
              <Route path="kyc" element={<KycPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            <Route path="/business-portal/register" element={<RequireAuth><BusinessRegisterPage /></RequireAuth>} />
            <Route path="/business-portal" element={<BusinessLayout />}>
              <Route index element={<BusinessDashboardPage />} />
              <Route path="mass-payments" element={<MassPaymentsPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
            </Route>

            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminOverviewPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="kyc" element={<AdminKycQueuePage />} />
              <Route path="aml" element={<AdminAmlPage />} />
              <Route path="rates" element={<AdminRatesPage />} />
              <Route path="deposits" element={<AdminDepositsPage />} />
              <Route path="withdrawals" element={<AdminWithdrawalsPage />} />
              <Route path="transactions" element={<AdminTransactionsPage />} />
              <Route path="companies" element={<AdminCompaniesPage />} />
              <Route path="audit" element={<AdminAuditLogPage />} />
              <Route path="admins" element={<AdminAdminsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
